import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAdminConfig, updateAdminConfig } from '../../services/adminConfigService';
import './AdminConfiguration.css';

const RATE_LIMIT_ENABLED_KEY = 'resume_eval.anonymous_rate_limit_enabled';

function rowsToDraft(rows) {
  const draft = {};
  rows.forEach((row) => {
    if (row.editable) draft[row.key] = row.value ?? '';
  });
  return draft;
}

function ConfigValueInput({ row, value, disabled, onChange }) {
  if (!row.editable) {
    return <span className="admin-config__readonly">{value}</span>;
  }
  if (row.valueType === 'boolean') {
    return (
      <label className="admin-config__checkbox">
        <input
          type="checkbox"
          checked={value === 'true'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        />
        <span>{value === 'true' ? 'true' : 'false'}</span>
      </label>
    );
  }
  if (row.valueType === 'integer') {
    return (
      <input
        className="admin-config__input"
        type="number"
        min={row.minValue ?? undefined}
        max={row.maxValue ?? undefined}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      className="admin-config__input admin-config__input--text"
      type="text"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function AdminConfiguration() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const rateLimitEnabled = draft[RATE_LIMIT_ENABLED_KEY] === 'true';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await fetchAdminConfig();
      setRows(data);
      setDraft(rowsToDraft(data));
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to load configuration.');
      setRows([]);
      setDraft({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const editableRows = useMemo(() => rows.filter((row) => row.editable), [rows]);

  const onChange = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setSuccess('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const data = await updateAdminConfig(draft);
      setRows(data);
      setDraft(rowsToDraft(data));
      setSuccess('Configuration saved.');
    } catch (err) {
      setError(
        err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save configuration.'
      );
    } finally {
      setSaving(false);
    }
  };

  const isRowDisabled = (row) => {
    if (!row.editable) return true;
    if (row.key === RATE_LIMIT_ENABLED_KEY) return false;
    if (row.key.startsWith('resume_eval.') && !rateLimitEnabled) return true;
    return false;
  };

  return (
    <div className="admin-config">
      <header className="admin-config__header">
        <h1 className="admin-config__title">Configuration</h1>
        <p className="admin-config__lead">
          Application settings keyed for easy extension. Add a row in the backend registry when introducing new values.
        </p>
      </header>

      {error ? <div className="admin-config__error">{error}</div> : null}
      {success ? <div className="admin-config__success">{success}</div> : null}

      {loading ? (
        <p className="admin-config__muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="admin-config__muted">No configuration items.</p>
      ) : (
        <form onSubmit={onSubmit}>
          <div className="admin-config__table-wrap">
            <table className="admin-config__table">
              <thead>
                <tr>
                  <th scope="col">Key</th>
                  <th scope="col">Description</th>
                  <th scope="col">Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td className="admin-config__key">
                      <code>{row.key}</code>
                    </td>
                    <td className="admin-config__description">{row.description}</td>
                    <td className="admin-config__value">
                      <ConfigValueInput
                        row={row}
                        value={draft[row.key] ?? row.value ?? ''}
                        disabled={isRowDisabled(row)}
                        onChange={(value) => onChange(row.key, value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {editableRows.length > 0 ? (
            <button type="submit" className="admin-config__save" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          ) : null}
        </form>
      )}
    </div>
  );
}
